/**
 * SiteFooter — global footer rendered on every route via App.js.
 *
 * Shows the Jack Royals LLC business mailing address required by
 * sweepstakes compliance regs (owner-mandated Feb 2026). Hidden on
 * game-player routes and admin to avoid cramping iframe/dashboard UX.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";

const HIDE_ON_PREFIXES = ["/game/", "/admin", "/game-player"];

export default function SiteFooter() {
  const { pathname } = useLocation();
  if (HIDE_ON_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <footer
      className="border-t border-white/10 bg-[#07080c] text-slate-400 mt-10"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
          {/* Business entity + mailing address */}
          <div data-testid="site-footer-address">
            <div className="text-white font-black tracking-tight text-base mb-2">
              Jack Royals LLC
            </div>
            <div className="flex items-start gap-2 text-xs sm:text-sm leading-relaxed">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400/80" />
              <address className="not-italic">
                30 N Gould St Ste N<br />
                Sheridan, WY 82801<br />
                United States
              </address>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm mt-3">
              <Mail className="w-3.5 h-3.5 flex-shrink-0 text-amber-400/80" />
              <a
                href="mailto:support@jackroyals.com"
                className="hover:text-emerald-300 transition-colors"
                data-testid="site-footer-support-email"
              >
                support@jackroyals.com
              </a>
            </div>
          </div>

          {/* Compliance / policy links */}
          <div>
            <div className="text-white font-bold text-xs uppercase tracking-wider mb-3">
              Legal
            </div>
            <ul className="space-y-1.5 text-xs sm:text-sm">
              <li><Link to="/terms" className="hover:text-emerald-300 transition-colors">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-emerald-300 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/sweepstakes-rules" className="hover:text-emerald-300 transition-colors">Sweepstakes Rules</Link></li>
              <li><Link to="/amoe" className="hover:text-emerald-300 transition-colors">AMOE (No Purchase Entry)</Link></li>
              <li><Link to="/responsible-gaming" className="hover:text-emerald-300 transition-colors">Responsible Gaming</Link></li>
            </ul>
          </div>

          {/* Help links */}
          <div>
            <div className="text-white font-bold text-xs uppercase tracking-wider mb-3">
              Support
            </div>
            <ul className="space-y-1.5 text-xs sm:text-sm">
              <li><Link to="/contact-us" className="hover:text-emerald-300 transition-colors">Contact Us</Link></li>
              <li><Link to="/faq" className="hover:text-emerald-300 transition-colors">FAQ</Link></li>
              <li><Link to="/payout-policy" className="hover:text-emerald-300 transition-colors">Payout Policy</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-emerald-300 transition-colors">Shipping Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-emerald-300 transition-colors">Refund Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 mt-7 pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[11px] sm:text-xs text-zinc-500">
            &copy; {new Date().getFullYear()} Jack Royals LLC. All rights reserved. No purchase necessary. Void where prohibited.
          </p>
          <p className="text-[10px] sm:text-xs text-zinc-600">
            18+ only. Please play responsibly.
          </p>
        </div>
      </div>
    </footer>
  );
}
